import {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BroadcastQueueAccordion from './BroadcastQueueAccordion';
import Pagination from '../../../../components/common/Pagination/Pagination';
import useGetFleetId from '../../../../API/hooks/useGetFleetId';
import useGetCurrentPage from '../../../../API/hooks/useGetCurrentPage';
import useGetAudioAnnouncementSendHistory from '../../../../API/hooks/announcement/useGetAudioAnnouncementSendHistory';
import useAbortBroadcast from '../../../../API/hooks/announcement/useAbortBroadcast';
import { getLeftPeriodLabel } from '../../../../utils/dateUtils';
import { toast } from '../../../../utils/toast';
import S from './BroadcastQueueStyles';
import type {
  AbortBroadcastParams,
  BroadcastQueueItem as BroadcastQueueItemType,
} from './types';
import type { SendHistoryItem, AbortAudioResponse } from '../../../../API/types/AudioAnnouncement';
import {
  BROADCAST_STATUS_TABS,
  statusToTabId,
} from './constants';

const PAGE_LIMIT = 20;
const SORT_ORDER = 'DESC';
const DEVICE_STATUS_PLACEHOLDER = '—';

function buildSummary(createdAt: string | undefined, totalDevices: number | undefined): string {
  if (!createdAt) return '';
  const timeAgo = getLeftPeriodLabel(createdAt);
  const count = totalDevices ?? 0;
  return `Sent ${timeAgo} to ${count} device${count === 1 ? '' : 's'}`;
}

function sendHistoryToQueueItem(item: SendHistoryItem): BroadcastQueueItemType {
  const id = item.sendId ?? item._id ?? '';
  const filename = item.fileName ?? id;
  const devices = item.devices ?? [];
  const countByTab: Record<string, number> = {};
  BROADCAST_STATUS_TABS.forEach((tab) => {
    countByTab[tab.id] = 0;
  });
  devices.forEach((d) => {
    const tabId = statusToTabId(d.status);
    if (countByTab[tabId] !== undefined) {
      countByTab[tabId] += 1;
    } else {
      countByTab[tabId] = 1;
    }
  });
  const tabs = BROADCAST_STATUS_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    count: countByTab[tab.id] ?? 0,
  }));
  const deviceRows = devices.map((d) => ({
    id: `${d.deviceId}-${d.requestNum}`,
    deviceId: d.deviceId,
    messageStatus: d.status,
    deviceStatus: DEVICE_STATUS_PLACEHOLDER,
  }));
  return {
    id,
    filename,
    summary: buildSummary(item.createdAt, item.totalDevices ?? devices.length),
    tabs,
    devices: deviceRows,
    presignedUrl: item.presignedUrl ?? null,
    audioAnnouncementId: item.audioAnnouncementId ?? '',
  };
}

export default function BroadcastQueue(): JSX.Element {
  const fleetId = useGetFleetId();
  const urlPage = useGetCurrentPage();
  const [page, setPage] = useState(urlPage);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setPage(urlPage);
  }, [urlPage]);

  const navigateToPage = useCallback((targetPage: number): void => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', targetPage.toString());
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    setPage(targetPage);
  }, [navigate, location.pathname, location.search]);

  const changePage = useCallback((paginationAction: (prev: number) => number): void => {
    const targetPage = paginationAction(page);
    if (targetPage >= 1) {
      navigateToPage(targetPage);
    }
  }, [page, navigateToPage]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    prevPage,
    nextPage,
    totalPages,
  } = useGetAudioAnnouncementSendHistory(
    fleetId ?? null,
    page,
    PAGE_LIMIT,
    SORT_ORDER,
  );

  useEffect(() => {
    if (totalPages != null && totalPages > 0 && page > totalPages) {
      navigateToPage(totalPages);
    } else if (totalPages === 0 && !isLoading && !data?.results?.length && page > 1) {
      navigateToPage(1);
    }
  }, [data, totalPages, page, isLoading, navigateToPage]);

  const queueItems = useMemo((): BroadcastQueueItemType[] => {
    if (!data?.results?.length) return [];
    return data.results.map(sendHistoryToQueueItem);
  }, [data?.results]);

  const handleAbortSuccess = useCallback((res: AbortAudioResponse): void => {
    const aborted = res.abortedDevices?.length ?? 0;
    const notInQueued = res.notInQueuedState?.length ?? 0;
    const notFound = res.notFoundDevices?.length ?? 0;
    const gatewayFailed = res.gatewayFailed?.length ?? 0;
    const failed = notInQueued + notFound + gatewayFailed;
    if (failed > 0) {
      toast.error(aborted > 0
        ? `Aborted ${aborted} device(s). ${failed} could not be aborted.`
        : 'Some devices could not be aborted.');
    } else {
      toast.success(aborted > 0 ? `Aborted ${aborted} device(s).` : 'Done.');
    }
    setExpandedId(null);
  }, []);

  const handleAbortFailure = useCallback((errorMessage: string): void => {
    toast.error(errorMessage ?? 'Abort failed');
  }, []);

  const abortBroadcastMutation = useAbortBroadcast(
    handleAbortSuccess,
    handleAbortFailure,
  );

  const handleToggle = useCallback((id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleAbort = useCallback((params: AbortBroadcastParams): void => {
    const { sendId, audioAnnouncementId, deviceIds } = params;
    if (!fleetId || !audioAnnouncementId || !sendId) return;
    if (!deviceIds.length) return;
    abortBroadcastMutation.mutate({
      fleetId,
      audioId: audioAnnouncementId,
      sendId,
      devices: deviceIds.map((deviceId) => ({ deviceId })),
    });
  }, [fleetId, abortBroadcastMutation]);

  const handlePlayPause = useCallback((itemId: string, isPlaying: boolean): void => {
    setPlayingId(isPlaying ? itemId : null);
  }, []);

  return (
    <S.Section aria-label="Broadcast Queue and Status">
      <S.SectionTitle>Broadcast Queue &amp; Status</S.SectionTitle>
      {isLoading && <p>Loading...</p>}
      {isError && <p>Failed to load broadcast queue.</p>}
      {!isLoading && !isError && queueItems.length > 0 && (
        <>
          <BroadcastQueueAccordion
            fleetId={fleetId ?? null}
            items={queueItems}
            expandedId={expandedId}
            onToggle={handleToggle}
            onAbort={handleAbort}
            playingId={playingId}
            onPlayPause={handlePlayPause}
            isAbortLoading={abortBroadcastMutation.isLoading}
          />
          <Pagination
            setPage={changePage}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={page}
            totalPages={totalPages ?? undefined}
            isFetching={isFetching}
          />
        </>
      )}
      {!isLoading && !isError && queueItems.length === 0 && (
        <p>No broadcast history yet.</p>
      )}
    </S.Section>
  );
}
